import { useCallback, useState } from "react";
import { IntlShape } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import { getCustomText } from "shared/lib/golCopy";
import { BookingFormPassengerSection } from "shared/types";

import {
  setBookingFormFieldValue,
  setBookingFormValue,
} from "../data/actions/bookingForm";
import { AppState } from "../data/reducers";
import { useCbtGuestData } from "./cbtGuestData";

export interface BookingFormSectionProps {
  index: number;
  intl: IntlShape;
  query: any;
}

export function useBookingFormSection({
  index,
  intl,
}: BookingFormSectionProps) {
  const {
    cbt,
    cbtGuestsInDynamicFormShowingSearchForm,
    cbtGuestsUsedInDynamicForm,
    phonePrefix,
    allowedTravelerTypes,
    bookingFormSections,
    bookingFormSection,
    // eslint-disable-next-line prefer-const
    bookingFormFields,
    isFetchingServices,
    backgroundImageUrl,
    rooms,
  } = useSelector((state: AppState) => ({
    cbt: state.cbt,
    cbtGuestsInDynamicFormShowingSearchForm:
      state.bookingForm.cbtGuestsInDynamicFormShowingSearchForm,
    cbtGuestsUsedInDynamicForm: state.bookingForm.cbtGuestsUsedInDynamicForm,
    phonePrefix:
      state.storage.frontendSettings.dealerFrontendSettings?.phonePrefix,
    allowedTravelerTypes: state.requestorConfig.allowedTravelerTypes,
    bookingFormSections: state.bookingForm.bookingFormSections,
    bookingFormSection: state.bookingForm.bookingFormSections[index],
    bookingFormFields: state.bookingForm.bookingFormFields,
    isFetchingServices: state.bookingForm.isFetchingServices,
    backgroundImageUrl:
      state.storage.frontendSettings.dealerFrontendSettings?.background?.image
        ?.url,
    rooms: state.search.searchHotels.rooms,
  }));

  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState<{
    content?: string;
    title?: string;
    alternateUrl?: string;
    showErrorText?: boolean;
  }>({
    content: "",
    title: "",
    alternateUrl: "",
    showErrorText: false,
  });
  const dispatch = useDispatch();
  let bookingFormSectionModified = bookingFormSection;

  const isHotelRoomForm = bookingFormSections.some(
    (section) =>
      typeof section?.room === "string" && section?.room?.includes("room")
  );

  const indexPassenger = getIndexPassengerFromBookingFormSection(
    bookingFormSection
  );

  const cbtGuestData = useCbtGuestData({
    index,
    indexPassenger,
  });

  const toggleIsCollapsed = () => {
    dispatch(
      setBookingFormValue(
        "bookingFormSections",
        bookingFormSections.map((bookingFormSectionInside, indexSection) => {
          if (indexSection !== index || !bookingFormSectionInside.collapsible) {
            return bookingFormSectionInside;
          }

          return {
            ...bookingFormSectionInside,
            isCollapsed: !bookingFormSectionInside.isCollapsed,
          };
        })
      )
    );
  };

  const hiddenFields = ["UserPassword", "UserLogin"];

  const nthInRoom = isHotelRoomForm
    ? bookingFormSections
        .filter(
          (section) =>
            section.room === bookingFormSection.room &&
            section.code === "traveler"
        )
        .findIndex((section) => section === bookingFormSection)
    : undefined;

  const isTravelerInRoom = isHotelRoomForm
    ? typeof bookingFormSection?.room === "string" &&
      bookingFormSection?.code === "traveler" &&
      cbt.passengersForm?.hotelRoomAssignmentsBookingProcess?.[
        bookingFormSection?.room?.split("-")[0]
      ]?.[nthInRoom] !== undefined
    : false;

  const isGuest =
    (!isHotelRoomForm &&
      cbt?.passengersForm?.travellers &&
      indexPassenger >= cbt.passengersForm.travellers.length) ||
    (isHotelRoomForm &&
      !isTravelerInRoom &&
      bookingFormSection?.code === "traveler");

  const showUpdateCbtGuestForLater =
    isGuest &&
    cbt.corporate?.AllowGuestFrontendSave &&
    cbtGuestsUsedInDynamicForm?.[indexPassenger];

  const uneditablePassengerName = getUneditablePassengerName({
    bookingFormSection,
    cbt,
    bookingFormFields,
    showUpdateCbtGuestForLater,
    isHotelRoomForm,
    bookingFormSections,
    rooms,
  });

  const title = getTitle({
    intl,
    title: bookingFormSection.title,
    allowedTravelerTypes,
  });

  const showLCCText = getShowLCCText({
    bookingFormSection,
  });

  const showBookingFormSection = getShowBookingFormSection({
    bookingFormSection,
    cbt,
    cbtGuestsInDynamicFormShowingSearchForm,
  });

  const showCbtGuestAddNewGuestButton =
    isGuest &&
    !(cbtGuestData.showCbtGuestSearchForm && !showUpdateCbtGuestForLater) &&
    cbt.guestsCache?.length > 0;

  const deletePredefinedGuest = () => {
    if (cbtGuestsUsedInDynamicForm?.[indexPassenger]) {
      const newGuestsUsedInDynamicForm = cbtGuestsUsedInDynamicForm;
      delete newGuestsUsedInDynamicForm[indexPassenger];

      dispatch(
        setBookingFormValue(
          "cbtGuestsUsedInDynamicForm",
          newGuestsUsedInDynamicForm
        )
      );
    }
    bookingFormSection.elements.forEach((elementName) => {
      dispatch(
        setBookingFormFieldValue(elementName, {
          ...bookingFormFields[elementName],
          value: elementName.includes("phone_country") ? phonePrefix : "",
        })
      );
    });
  };

  const setShowCbtGuestSearchForm = (showForm) => {
    if (!cbtGuestsInDynamicFormShowingSearchForm && showForm) {
      return dispatch(
        setBookingFormValue("cbtGuestsInDynamicFormShowingSearchForm", [
          indexPassenger,
        ])
      );
    }

    const newGuestsInDynamicFormShowingSearchForm = showForm
      ? cbtGuestsInDynamicFormShowingSearchForm.concat(indexPassenger)
      : cbtGuestsInDynamicFormShowingSearchForm.filter(
          (indexInside) => indexInside !== indexPassenger
        );

    return dispatch(
      setBookingFormValue(
        "cbtGuestsInDynamicFormShowingSearchForm",
        newGuestsInDynamicFormShowingSearchForm
      )
    );
  };

  const showSaveCbtGuestForLater =
    isGuest &&
    cbt.corporate?.AllowGuestFrontendSave &&
    !cbtGuestsUsedInDynamicForm?.[indexPassenger] &&
    !cbtGuestData.showCbtGuestSearchForm;

  const modalSectionHandler = useCallback(
    async (textName: string) => {
      await setShowModal((prevShowModal) => !prevShowModal);
      if (!showModal && textName) {
        const modalDataRes = await getCustomText(textName);
        setModalData(modalDataRes);
      }
    },
    [showModal]
  );

  const indexTitle = bookingFormSection.elements.findIndex((element) =>
    element.includes("title")
  );
  const indexLastName = bookingFormSection.elements.findIndex((element) =>
    element.includes("lastname")
  );
  const indexFirstName = bookingFormSection.elements.findIndex((element) =>
    element.includes("firstname")
  );

  const isBillingSection = bookingFormSection?.code === "billing";

  if (isBillingSection) {
    const isSomeRequiredField = bookingFormSection?.elements.some(
      (elementName) => {
        return bookingFormFields[elementName].required === true;
      }
    );

    if (isSomeRequiredField) {
      bookingFormSectionModified = {
        ...bookingFormSectionModified,
        collapsible: false,
        isCollapsed: false,
      };
    }
  }

  if (bookingFormSectionModified && bookingFormSectionModified.room) {
    const currentRoom = bookingFormSectionModified.room;
    const firstInRoomIndex = bookingFormSections.findIndex(
      (section) => section.room === currentRoom && section.code === "traveler"
    );
    if (
      firstInRoomIndex !== -1 &&
      bookingFormSections[firstInRoomIndex] === bookingFormSectionModified
    ) {
      bookingFormSectionModified = {
        ...bookingFormSectionModified,
        isFirstInRoomSelection: true,
      };
    } else {
      bookingFormSectionModified = {
        ...bookingFormSectionModified,
        isFirstInRoomSelection: false,
      };
    }
  }

  return {
    toggleIsCollapsed,
    bookingFormSection: !uneditablePassengerName
      ? bookingFormSectionModified
      : {
          ...bookingFormSectionModified,
          elements: bookingFormSectionModified.elements.filter(
            (_, elIndex) =>
              elIndex !== indexTitle &&
              elIndex !== indexLastName &&
              elIndex !== indexFirstName
          ),
        },
    uneditablePassengerName,
    title,
    ...cbtGuestData,
    showCbtGuestSearchForm:
      cbtGuestData.showCbtGuestSearchForm && !showUpdateCbtGuestForLater,
    showBookingFormSection,
    showUpdateCbtGuestForLater,
    showCbtGuestAddNewGuestButton,
    deletePredefinedGuest,
    setShowCbtGuestSearchForm,
    showSaveCbtGuestForLater,
    indexGuest: indexPassenger,
    indexPassenger,
    showLCCText,
    showModal,
    modalSectionHandler,
    modalData,
    hiddenFields,
    isFetchingServices,
    backgroundImageUrl,
    isHotelRoomForm,
  };
}

export function getTitle({ title, intl, allowedTravelerTypes }) {
  if (!title) return false;

  const code =
    typeof title === "string" && title.startsWith("CODE")
      ? title.split("-")[2]
      : null;

  const getPassengerTitle = (suffix = "") =>
    `${intl.formatMessage({ id: "Passenger.title" })}${suffix}`;

  if (code && allowedTravelerTypes?.length > 0) {
    const selectedTravelerType = allowedTravelerTypes.find(
      (travelerType) => travelerType.Code === code
    );
    if (selectedTravelerType)
      return getPassengerTitle(` - ${selectedTravelerType.$t}`);
  }

  if (code) {
    return getPassengerTitle(
      ` - ${intl.formatMessage({ id: `PassengerCodes.${code}` })}`
    );
  }

  return title;
}

function getShowLCCText({ bookingFormSection }) {
  if (bookingFormSection?.code !== "other") {
    return false;
  }

  const otherSectionElements = bookingFormSection?.elements;

  // eslint-disable-next-line no-unused-vars
  const isLCC =
    otherSectionElements.includes("UserPassword") &&
    otherSectionElements.includes("UserLogin");

  // Temporary disabled
  // return isLCC;
  return false;
}

function getUneditablePassengerName({
  bookingFormSection,
  cbt,
  bookingFormFields,
  showUpdateCbtGuestForLater,
  isHotelRoomForm,
  bookingFormSections,
  rooms,
}) {
  if (
    typeof bookingFormSection?.title === "string" &&
    !bookingFormSection.title.startsWith("CODE-")
  ) {
    return false;
  }

  const countAllGuests = () => {
    if (!rooms || !Array.isArray(rooms)) return 0;
    return rooms.reduce((total, room) => total + (room.guests || 0), 0);
  };

  if (
    !cbt ||
    !cbt.passengersForm ||
    (cbt.passengersForm.travellers?.length ?? 0) +
      (cbt.passengersForm.guests ?? 0) +
      countAllGuests() ===
      0
  ) {
    return false;
  }

  const indexPassenger = getIndexPassengerFromBookingFormSection(
    bookingFormSection
  );

  if (
    !cbt.passengersForm.travellers ||
    (!isHotelRoomForm &&
      indexPassenger >= cbt.passengersForm.travellers.length &&
      !showUpdateCbtGuestForLater)
  ) {
    return false;
  }

  if (isHotelRoomForm) {
    const roomAssignments =
      cbt.passengersForm?.hotelRoomAssignmentsBookingProcess;
    if (!roomAssignments) {
      return false;
    }

    const roomIndexStr = bookingFormSection.room?.split("-")[0];
    if (roomIndexStr === undefined) {
      return false;
    }

    const numberOfTravellersInRoom = roomAssignments[roomIndexStr]?.length || 0;
    if (numberOfTravellersInRoom === 0) {
      return false;
    }

    const travelerSectionsInRoom = bookingFormSections.filter(
      (section) =>
        section.room === bookingFormSection.room && section.code === "traveler"
    );

    const passengerIndexInRoom = travelerSectionsInRoom.findIndex(
      (section) => section === bookingFormSection
    );

    if (
      passengerIndexInRoom === -1 ||
      passengerIndexInRoom >= numberOfTravellersInRoom
    ) {
      return false;
    }
  }

  const titleField =
    bookingFormFields[
      bookingFormSection.elements.find((el) => el.includes("_title"))
    ];
  const firstNameField =
    bookingFormFields[
      bookingFormSection.elements.find((el) => el.includes("_firstname"))
    ];
  const lastNameField =
    bookingFormFields[
      bookingFormSection.elements.find((el) => el.includes("_lastname"))
    ];

  const titleObj = titleField.options.find(
    (option) => option.value.toLowerCase() === titleField.value.toLowerCase()
  );

  return `${titleObj ? titleObj.label : ""} ${firstNameField.value} ${
    lastNameField.value
  }`;
}

// hides cbt guest not filled but able to be suggested
function getShowBookingFormSection({
  bookingFormSection,
  cbt,
  cbtGuestsInDynamicFormShowingSearchForm,
}) {
  if (
    cbt?.corporate === null &&
    cbt?.costCenters.length === 0 &&
    cbt?.users.length === 0
  ) {
    return true;
  }

  if (!cbt?.passengersForm?.guests || !cbt.passengersForm?.travellers) {
    return true;
  }

  if (
    !cbt ||
    (cbt.passengersForm?.guests || 0) +
      (cbt.passengersForm?.travellers?.length || 0) ===
      0
  ) {
    return true;
  }

  const indexPassenger = getIndexPassengerFromBookingFormSection(
    bookingFormSection
  );

  if (
    bookingFormSection.code === "passengerServices" &&
    indexPassenger >= cbt.passengersForm.travellers.length &&
    cbtGuestsInDynamicFormShowingSearchForm.includes(indexPassenger)
  ) {
    return false;
  }

  return true;
}

export function getIndexPassengerFromBookingFormSection(
  bookingFormSection: BookingFormPassengerSection
): number {
  // eslint-disable-next-line eqeqeq
  if (!bookingFormSection.elements || bookingFormSection.elements.length == 0) {
    return 0;
  }
  return Number(bookingFormSection.elements[0].split("-")[0]);
}
